import React from "react";
import {
  calculateAndFormat,
  calculateAndFormatNoAbs,
} from "../../component/Methods";

/** Returns true if v is a valid number (not null, undefined, or NaN). */
export const isValidNum = (v) =>
  v !== null && v !== undefined && !isNaN(Number(v));

/** Returns green/red color string by sign. Pass theme and optionally invert (e.g. for short performance). */
export const getSignColor = (value, theme, invert = false) => {
  const positive = invert ? value <= 0 : value >= 0;
  return positive
    ? theme.palette.mode === "dark"
      ? "#4ade80"
      : "#16a34a"
    : theme.palette.mode === "dark"
      ? "#f87171"
      : "#dc2626";
};

/** Format as currency with parentheses for negative; empty string if invalid. */
export const formatCurrency = (value) =>
  isValidNum(value)
    ? value >= 0
      ? <>{calculateAndFormat(value)}&nbsp;</>
      : `(${calculateAndFormat(value * -1)})`
    : "";

/** Compact USD for AUM: $1.2M / $850K / $500; parentheses for negative. */
export const formatCompactCurrency = (value) => {
  if (!isValidNum(value)) return "";
  const n = Number(value);
  const abs = Math.abs(n);
  let compact;
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    compact = `$${m >= 10 ? m.toFixed(1) : m.toFixed(2)}M`.replace(
      /\.?0+M$/,
      "M"
    );
  } else if (abs >= 1_000) {
    const k = abs / 1_000;
    compact = `$${k >= 10 ? k.toFixed(1) : k.toFixed(2)}K`.replace(
      /\.?0+K$/,
      "K"
    );
  } else {
    compact = `$${Math.round(abs).toLocaleString("en-US")}`;
  }
  return n >= 0 ? compact : `(${compact})`;
};

/** Format as plain percentage (e.g. sizeLong); empty if invalid. */
export const formatPercentPlain = (value) =>
  isValidNum(value) ? `${parseFloat(value).toFixed(2)}%` : "";

/** Format as signed percentage: X.XX% or (X.XX%) for negative. */
export const formatPercentSigned = (value) =>
  isValidNum(value)
    ? value < 0
      ? `(${Math.abs(value).toFixed(2)}%)`
      : `${value.toFixed(2)}%`
    : "";

/** Long capital: NoAbs when >= 0, parentheses when negative. */
export const formatLongCapital = (value) =>
  isValidNum(value)
    ? value >= 0
      ? <>{calculateAndFormatNoAbs(value)}&nbsp;</>
      : `(${calculateAndFormat(value * -1)})`
    : "";

/** Short capital: display as NoAbs(value * -1) or 0 when value is 0. */
export const formatShortCapital = (value) =>
  isValidNum(value)
    ? value
      ? <>{calculateAndFormatNoAbs(value * -1)}&nbsp;</>
      : 0
    : "";

/** Net capital: NoAbs with parentheses for negative. */
export const formatNetCapital = (value) =>
  isValidNum(value)
    ? value < 0
      ? `(${calculateAndFormatNoAbs(value * -1)})`
      : <>{calculateAndFormatNoAbs(value)}&nbsp;</>
    : "";

/** Percentage for colored cells: X.XX% or (X.XX%) as JSX. */
export const formatPercentColored = (value) =>
  isValidNum(value)
    ? value >= 0
      ? <>{value.toFixed(2)}%&nbsp;</>
      : <>({Math.abs(value).toFixed(2)}%)</>
    : "";
